import { useState } from 'react'
import { Button, InlineAlert } from '@design-systems/canvas-ui'
import Icon, {
  CheckmarkIcon,
  MerchBooksIcon,
  PencilIcon,
} from '@design-systems/react-icons'
import ExtractionStatusNotice from './ExtractionStatusNotice'
import {
  BRAND_LIFECYCLE_STATUS,
  isActive,
  isDraft,
} from '../config/brandLifecycleConfig'

const BrandProfileView = ({
  canEdit,
  error,
  isLoading,
  isSaving,
  lifecycleStatus,
  onOpenReview,
  onSave,
  onStartUploading,
  profile,
  requiredDocumentLabels,
}) => {
  // `null` while not editing, otherwise the edited text keyed by section id.
  const [drafts, setDrafts] = useState(null)

  if (isDraft(lifecycleStatus)) {
    return (
      <section aria-label="Brand Profile" className="brand-profile-empty">
        <span className="brand-profile-empty__icon">
          <Icon size="lg" svg={MerchBooksIcon} />
        </span>
        <h2>Brand profile is empty</h2>
        <p>
          Upload your brand documents and review the extracted sections to
          populate this profile.
        </p>
        {requiredDocumentLabels.length > 0 && (
          <small>Required: {requiredDocumentLabels.join(' · ')}</small>
        )}
        <Button onClick={onStartUploading} variant="primary">
          Start uploading
        </Button>
      </section>
    )
  }
  // No profile exists until the review is done, so the tab points the way in.
  if (
    lifecycleStatus === BRAND_LIFECYCLE_STATUS.EXTRACTION_IN_PROGRESS ||
    lifecycleStatus === BRAND_LIFECYCLE_STATUS.IN_REVIEW
  ) {
    return (
      <ExtractionStatusNotice
        lifecycleStatus={lifecycleStatus}
        onOpenReview={onOpenReview}
      />
    )
  }
  // Reviewed and handed over, but not live yet — the profile API has nothing
  // to return until the brand is ACTIVE.
  if (!isActive(lifecycleStatus)) {
    return (
      <section aria-label="Brand Profile" className="brand-profile-empty">
        <span className="brand-profile-empty__icon">
          <Icon size="lg" svg={MerchBooksIcon} />
        </span>
        <h2>Brand profile is setting up</h2>
        <p>
          Your approved sections will appear here once the brand is published.
          Check back in a few moments.
        </p>
      </section>
    )
  }
  if (isLoading) {
    return <p className="draft-review__empty">Loading brand profile…</p>
  }
  if (error) {
    return <InlineAlert variant="error">{error}</InlineAlert>
  }
  if (!profile) return null

  const sections = profile.groups.flatMap((group) => group.sections)
  const isEditing = drafts !== null

  // Only changed sections are sent; the API carries every other one forward.
  const changed = isEditing
    ? sections
        .filter(({ id, value }) => drafts[id].trim() !== value)
        .map(({ id }) => ({ id, value: drafts[id].trim() }))
    : []

  const saveChanges = async () => {
    if (await onSave(changed)) setDrafts(null)
  }

  return (
    <section aria-label="Brand Profile" className="brand-profile">
      <header className="brand-profile__header">
        <div>
          <h2>Brand Profile</h2>
          <p>{profile.sectionCount} approved sections</p>
        </div>
        {canEdit && !isEditing && (
          <Button
            onClick={() =>
              setDrafts(
                Object.fromEntries(
                  sections.map(({ id, value }) => [id, value]),
                ),
              )
            }
            variant="secondary"
          >
            <span className="upload-documents__button-content">
              <Icon size="sm" svg={PencilIcon} /> Edit
            </span>
          </Button>
        )}
        {isEditing && (
          <div className="draft-section__actions">
            <Button
              isDisabled={isSaving}
              onClick={() => setDrafts(null)}
              variant="secondary"
            >
              Cancel
            </Button>
            {/* Enabled once something has changed. The API refuses a blank or
                whitespace-only value on any section it is sent. */}
            <Button
              isDisabled={
                isSaving ||
                changed.length === 0 ||
                changed.some(({ value }) => !value)
              }
              onClick={saveChanges}
              variant="primary"
            >
              <span className="upload-documents__button-content">
                <Icon size="sm" svg={CheckmarkIcon} /> Save
              </span>
            </Button>
          </div>
        )}
      </header>
      {profile.groups.map((group) => (
        <section className="brand-profile__group" key={group.id}>
          <header>
            <h3>{group.label}</h3>
            <span>{group.sections.length} sections</span>
          </header>
          <dl>
            {group.sections.map((section) => (
              <div key={section.id}>
                <dt>{section.title}</dt>
                <dd>
                  {isEditing ? (
                    <textarea
                      aria-label={`Edit ${section.title}`}
                      onChange={({ target }) =>
                        setDrafts((current) => ({
                          ...current,
                          [section.id]: target.value,
                        }))
                      }
                      rows={2}
                      value={drafts[section.id]}
                    />
                  ) : (
                    section.value
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </section>
  )
}

export default BrandProfileView
